export default function flowPreflight({ dispatch, services, stores }: CommandArgs) {
  const quoteBuilder: QuoteBuilder = services['quote-builder']
  const flowManager: FlowManager = services['flow-manager']
  const availabilityStore: AvailabilityStore = stores['availability']

  /**
   * Runs once after flow config loads, before any user interaction.
   * Sequences hydrators in dependency order, then attempts an initial quote creation.
   *
   * Order is load-bearing:
   *   1. tickets  — must pre-select before availability is fetched (ticket quantities affect slot availability)
   *   2. date     — fetches availability for the resolved date (param > program config)
   *   3. time     — sets time from ?timeString param, skipped if date auto-selected a time
   *   4. warm     — fire-and-forget current-range availability warm (see below)
   *   5. commit   — attempts createQuote if all required fields are now populated
   *   6. evaluate — re-runs step validation so allStepsValid reflects the hydrated state.
   *                 Steps are evaluated once on mount (before the async fetch returns), so
   *                 canAdvanceStep() is stale. Re-evaluating here unlocks the continue button.
   */
  async function runPreflight() {
    dispatch('hydrate/tickets:hydrateFromConfig')

    await dispatch('hydrate/date:hydrate')
    dispatch('hydrate/time:hydrateFromParams')

    // Fire-and-forget warm of the current range so the round-trip overlaps flow init + the
    // user's time on earlier steps, instead of sitting entirely in front of the date-picker
    // as skeleton once it mounts. Not awaited — must not delay quote commit / evaluateSteps.
    // Idempotent: when hydrate/date already resolved a date, setSelectedDate started the same
    // fetch and the engine dedupes the in-flight request; when no date resolved, this starts
    // the current-month fetch now so the date-picker's later ensureAvailabilityDates() call
    // resolves against a warm/in-flight entry.
    dispatch('availability:ensureAvailabilityDates')

    // The commit below includes `availabilityTimeId`, which resolves off `validTimeslots` — times
    // data, not dates. That was implicitly guaranteed while `setSelectedDate` awaited the whole
    // anchored scope; it now waits here, where the dependency actually is, rather than on every
    // date selection. Gated on a resolved date so the no-date path stays as unblocked as before.
    if (availabilityStore.selectedDate) await availabilityStore.availabilityDates.promise()

    quoteBuilder.include('activityId', 'tickets', 'availabilityTimeId').compose('bookingQuotes').tryCommit()
    await flowManager.evaluateSteps()
  }

  return { runPreflight }
}

declare global {
  export type FlowPreflightCommands = ReturnType<typeof flowPreflight>

  interface CommandRegistry {
    'flow/preflight': FlowPreflightCommands
  }
}
